/**
 * Live Best Life - Swiper.js Carousel
 * Initializes the live best life carousel with responsive breakpoints and fixed 352px slide width
 */

class LiveBestLifeCarousel {
	constructor(selector) {
		this.selector = selector;
		this.swiper = null;
		this.init();
	}

	init() {
		// Check if Swiper is loaded
		if (typeof Swiper === 'undefined') {
			console.error('Swiper.js is not loaded. Please ensure the library is included.');
			return;
		}

		// Check if element exists
		const element = document.querySelector(this.selector);
		if (!element) {
			console.warn(`Live best life carousel element not found: ${this.selector}`);
			return;
		}

		// Get section settings from data attributes
		this.getSectionSettings(element);
		this.initializeSwiper();
	}

	getSectionSettings(element) {
		// Read settings from data attributes
		this.settings = {
			enableAutoplay: element.dataset.enableAutoplay === 'true',
			autoplayDelay: parseFloat(element.dataset.autoplayDelay) || 3,
			transitionSpeed: parseInt(element.dataset.transitionSpeed) || 600,
			pauseOnHover: element.dataset.pauseOnHover === 'true',
			showArrows: element.dataset.showArrows === 'true',
			showDots: element.dataset.showDots === 'true',
			enableHoverEffects: element.dataset.enableHoverEffects === 'true'
		};

	}


	initializeSwiper() {
		const config = {
			// Enable infinite loop
			loop: true,
			loopFillGroupWithBlank: false,
			loopAdditionalSlides: 1,

			// Fixed slide width - one slide at a time
			slidesPerView: 'auto',
			spaceBetween: 24,

			// Autoplay based on settings
			autoplay: this.settings.enableAutoplay ? {
				delay: this.settings.autoplayDelay * 1000, // Convert seconds to milliseconds
				disableOnInteraction: false,
				pauseOnMouseEnter: this.settings.pauseOnHover,
				reverseDirection: false,
			} : false,

			// Speed for slide transitions
			speed: this.settings.transitionSpeed,

			// Center slides for better visual effect
			centeredSlides: true,

			// Touch and mouse interactions
			touchRatio: 1,
			simulateTouch: true,

			// Slides per group (one slide at a time)
			slidesPerGroup: 1,

			// Navigation
			navigation: this.settings.showArrows ? {
				nextEl: '.swiper-button-next',
				prevEl: '.swiper-button-prev',
			} : false,

			// Pagination
			pagination: this.settings.showDots ? {
				el: '.swiper-pagination',
				clickable: true,
				dynamicBullets: true,
			} : false,

			// Responsive breakpoints
			breakpoints: {
				320: {
					spaceBetween: 12,
					slidesPerView: 'auto',
					speed: this.settings.transitionSpeed * 0.8,
					slidesPerGroup: 1,
				},
				480: {
					spaceBetween: 12,
					slidesPerView: 'auto',
					speed: this.settings.transitionSpeed * 0.8,
					slidesPerGroup: 1,
				},
				768: {
					spaceBetween: 20,
					slidesPerView: 'auto',
					speed: this.settings.transitionSpeed,
					slidesPerGroup: 1,
				},
				1024: {
					spaceBetween: 20,
					slidesPerView: 'auto',
					speed: this.settings.transitionSpeed,
					slidesPerGroup: 1,
				},
				1200: {
					spaceBetween: 24,
					slidesPerView: 'auto',
					speed: this.settings.transitionSpeed,
					slidesPerGroup: 1,
				}
			},

			// Effect
			effect: 'slide',

			// Accessibility
			a11y: {
				enabled: true,
			},
		};

		try {
			this.swiper = new Swiper(this.selector, config);

			// Setup event listeners
			this.setupEventListeners();


			// Set slide widths after initialization
			this.setSlideWidths();

			// Show images after initialization
			this.showImages();

		} catch (error) {
			console.error('Error initializing live best life carousel:', error);
		}
	}

	setSlideWidths() {
		// Ensure all slides have the fixed 352px width
		const slides = document.querySelectorAll(`${this.selector} .swiper-slide`);
		slides.forEach(slide => {
			slide.style.width = '352px';
			slide.style.flexShrink = '0';
			slide.style.maxWidth = '352px';
			slide.style.minWidth = '352px';
		});

		// Also set responsive widths for mobile
		this.updateResponsiveWidths();
	}

	updateResponsiveWidths() {
		const width = window.innerWidth;
		const slides = document.querySelectorAll(`${this.selector} .swiper-slide`);

		slides.forEach(slide => {
			if (width <= 480) {
				slide.style.width = '240px';
				slide.style.maxWidth = '240px';
				slide.style.minWidth = '240px';
			} else if (width <= 768) {
				slide.style.width = '280px';
				slide.style.maxWidth = '280px';
				slide.style.minWidth = '280px';
			} else {
				slide.style.width = '352px';
				slide.style.maxWidth = '352px';
				slide.style.minWidth = '352px';
			}
		});
	}

	setupEventListeners() {
		if (!this.swiper) return;

		// Handle slide changes
		this.swiper.on('slideChange', () => {
			this.onSlideChange();
		});

		// Handle resize to update slide widths
		window.addEventListener('resize', () => {
			this.updateResponsiveWidths();
		});

		// Pause autoplay on focus (accessibility)
		const slides = document.querySelectorAll(`${this.selector} .swiper-slide`);
		slides.forEach(slide => {
			slide.addEventListener('focus', () => this.pauseAutoplay());
			slide.addEventListener('blur', () => this.resumeAutoplay());
		});
	}

	onSlideChange() {
		// Trigger custom event for external listeners
		const event = new CustomEvent('liveBestLifeSlideChange', {
			detail: {
				activeIndex: this.swiper.activeIndex,
				realIndex: this.swiper.realIndex
			}
		});
		document.dispatchEvent(event);
	}

	showImages() {
		setTimeout(() => {
			const images = document.querySelectorAll(`${this.selector} .life-card__img`);
			images.forEach(img => {
				img.style.opacity = '1';
			});
		}, 100);
	}

	pauseAutoplay() {
		if (this.swiper && this.swiper.autoplay) {
			this.swiper.autoplay.stop();
		}
	}

	resumeAutoplay() {
		if (this.swiper && this.swiper.autoplay) {
			this.swiper.autoplay.start();
		}
	}

	destroy() {
		if (this.swiper) {
			this.swiper.destroy(true, true);
			this.swiper = null;
		}
	}

	// Public API methods
	slideTo(index) {
		if (this.swiper) {
			this.swiper.slideTo(index);
		}
	}

	slideNext() {
		if (this.swiper) {
			this.swiper.slideNext();
		}
	}

	slidePrev() {
		if (this.swiper) {
			this.swiper.slidePrev();
		}
	}
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function () {
	// Find all live best life carousels and initialize them
	const carousels = document.querySelectorAll('.live-best-life-swiper');

	carousels.forEach((carousel, index) => {
		const sectionId = carousel.closest('[id^="shopify-section-"]')?.id;
		if (sectionId) {
			const selector = `#${sectionId} .live-best-life-swiper`;
			new LiveBestLifeCarousel(selector);
		}
	});
});

// Global function for manual initialization (if needed)
window.initLiveBestLifeCarousel = function (selector) {
	return new LiveBestLifeCarousel(selector);
};